<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();
const store = useStore();

const model = defineModel({ type: Object, required: true });

const isUploading = ref(false);
const fileInput = ref(null);

const mediaUrl = computed({
  get: () => model.value.mediaUrl || '',
  set: val => {
    model.value = { ...model.value, mediaUrl: val };
  },
});

const headerComponent = computed(() => {
  const components = model.value.template?.components || [];
  return components.find(c => c.type === 'HEADER') || {};
});

const mediaFormat = computed(() => headerComponent.value.format || 'IMAGE');

const triggerUpload = () => {
  fileInput.value?.click();
};

const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  isUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('media_file', file);

    // We need campaign ID for upload, but campaign isn't created yet.
    // Store the file URL locally via direct upload or use a temporary approach.
    // For now, create a temporary object URL and store the file for later upload.
    const objectUrl = URL.createObjectURL(file);
    model.value = {
      ...model.value,
      mediaUrl: objectUrl,
      mediaFile: file,
    };
    useAlert(t('CAMPAIGN.V2.WIZARD.MEDIA.UPLOAD_SUCCESS'));
  } catch {
    useAlert(t('CAMPAIGN.V2.WIZARD.MEDIA.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};
</script>

<template>
  <div class="max-w-2xl">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.V2.WIZARD.MEDIA.TITLE') }}
    </h2>
    <p class="mt-1 text-sm text-n-slate-11">
      {{ t('CAMPAIGN.V2.WIZARD.MEDIA.DESCRIPTION') }}
    </p>

    <div class="mt-6 space-y-6">
      <!-- URL input -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('CAMPAIGN.V2.WIZARD.MEDIA.URL_LABEL') }}
        </label>
        <input
          v-model="mediaUrl"
          type="url"
          class="w-full px-4 py-2 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none"
          :placeholder="t('CAMPAIGN.V2.WIZARD.MEDIA.URL_PLACEHOLDER')"
        />
      </div>

      <!-- File upload -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('CAMPAIGN.V2.WIZARD.MEDIA.UPLOAD_LABEL') }}
        </label>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          :accept="mediaFormat === 'IMAGE' ? 'image/*' : mediaFormat === 'VIDEO' ? 'video/*' : '*/*'"
          @change="handleFileUpload"
        />
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
          :disabled="isUploading"
          @click="triggerUpload"
        >
          {{ isUploading ? t('CAMPAIGN.V2.WIZARD.MEDIA.UPLOADING') : t('CAMPAIGN.V2.WIZARD.MEDIA.UPLOAD_BUTTON') }}
        </button>
      </div>

      <!-- Preview -->
      <div v-if="mediaUrl" class="mt-4">
        <img
          v-if="mediaFormat === 'IMAGE'"
          :src="mediaUrl"
          class="max-w-xs rounded-lg border border-n-weak"
          alt="Media preview"
        />
        <video
          v-else-if="mediaFormat === 'VIDEO'"
          :src="mediaUrl"
          class="max-w-xs rounded-lg border border-n-weak"
          controls
        />
        <div
          v-else
          class="p-4 bg-n-alpha-2 rounded-lg text-sm text-n-slate-11"
        >
          {{ mediaFormat }}: {{ mediaUrl }}
        </div>
      </div>
    </div>
  </div>
</template>
